/**
 * AIReviewStepCard - Center panel step review card for AI Review (MA-FE-073)
 * 
 * Displays step content with inline editing capability.
 * Edits persist through Mutation Engine.
 */

import React, { useState, useCallback, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  MessageSquare,
  HelpCircle,
  TextCursorInput,
  Shield,
  Calendar,
  PhoneForwarded,
  GitBranch,
  Circle,
  Edit3,
  Save,
  X,
  ArrowRight,
  Check,
  Loader2,
} from "lucide-react";

// Icon mapping
const NODE_TYPE_ICONS = {
  message: MessageSquare,
  question: HelpCircle,
  input: TextCursorInput,
  consent: Shield,
  booking: Calendar,
  transfer: PhoneForwarded,
  condition: GitBranch,
};

// Human-readable node type names
const NODE_TYPE_LABELS = {
  message: "Message",
  question: "Question",
  input: "Input Field",
  consent: "Consent",
  booking: "Booking",
  transfer: "Transfer",
  condition: "Condition",
};

/**
 * Editable Text Field Component
 */
function EditableField({
  label,
  value,
  field,
  nodeId,
  onSave,
  isSaving,
  multiline = false,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || "");
  
  // Sync edit value when prop changes
  useEffect(() => {
    setEditValue(value || "");
  }, [value]);
  
  const handleSave = useCallback(() => {
    if (editValue !== value) {
      onSave(nodeId, field, editValue);
    }
    setIsEditing(false);
  }, [nodeId, field, editValue, value, onSave]);
  
  const handleCancel = useCallback(() => {
    setEditValue(value || "");
    setIsEditing(false);
  }, [value]);
  
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  }, [handleSave, handleCancel, multiline]);
  
  if (isEditing) {
    return (
      <div className="space-y-2">
        <label className="text-xs font-medium text-slate-400 uppercase tracking-wide">
          {label}
        </label>
        {multiline ? (
          <Textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="bg-slate-800/50 border-slate-600 text-slate-100 min-h-[100px]"
            autoFocus
          />
        ) : (
          <Input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="bg-slate-800/50 border-slate-600 text-slate-100"
            autoFocus
          />
        )}
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700">
            {isSaving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
            Save
          </Button>
          <Button size="sm" variant="ghost" onClick={handleCancel} className="text-slate-400">
            <X className="h-3 w-3 mr-1" />
            Cancel
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="group">
      <label className="text-xs font-medium text-slate-400 uppercase tracking-wide">
        {label}
      </label>
      <div 
        className={cn(
          "mt-1 p-3 rounded-lg bg-slate-800/30 border border-slate-700/50",
          "hover:border-slate-600 cursor-pointer transition-colors relative"
        )}
        onClick={() => setIsEditing(true)}
      >
        <p className={cn(
          "text-slate-100",
          multiline ? "whitespace-pre-wrap" : ""
        )}>
          {value || <span className="text-slate-500 italic">Not set</span>}
        </p>
        <Edit3 className="absolute top-3 right-3 h-4 w-4 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </div>
  );
}

/**
 * Response Options Component
 */
function ResponseOptions({ responses, nodeId, onSave, isSaving }) {
  if (!responses || responses.length === 0) return null;
  
  return (
    <div className="space-y-2">
      <label className="text-xs font-medium text-slate-400 uppercase tracking-wide">
        Response Options
      </label>
      <div className="space-y-2">
        {responses.map((response, index) => (
          <div 
            key={response.id || index}
            className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/30 border border-slate-700/50"
          >
            <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center text-xs text-purple-300">
              {index + 1}
            </div>
            <div className="flex-1">
              <p className="text-slate-100">{response.label}</p>
              {response.acknowledgement && (
                <p className="text-xs text-slate-400 mt-1">
                  → {response.acknowledgement}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Routing Preview Component
 */
function RoutingPreview({ routing, reviewOrder }) {
  if (!routing || routing.length === 0) return null;
  
  // Map target node IDs to labels
  const nodeLabels = new Map(
    reviewOrder.map(n => [n.id, n.metadata?.label || n.node_type])
  );
  
  return (
    <div className="space-y-2">
      <label className="text-xs font-medium text-slate-400 uppercase tracking-wide">
        Routing
      </label>
      <div className="space-y-1">
        {routing.map((route, index) => {
          const targetLabel = nodeLabels.get(route.targetNodeId) || route.targetNodeId;
          const conditionLabel = route.condition 
            ? route.condition.replace("response.value == ", "").replace(/'/g, "")
            : "Default";
          
          return (
            <div 
              key={route.edgeId || index}
              className="flex items-center gap-2 px-3 py-2 rounded bg-slate-800/20 text-sm"
            >
              <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
                {conditionLabel}
              </Badge>
              <ArrowRight className="h-3 w-3 text-slate-500" />
              <span className="text-slate-300">{targetLabel}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Main Step Card Component
 */
export default function AIReviewStepCard({
  step,
  stepNumber,
  totalSteps,
  routing,
  reviewOrder,
  isApproved,
  isEdited,
  isSaving,
  onEdit,
}) {
  if (!step) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-slate-400">No step selected</p>
      </div>
    );
  }
  
  const Icon = NODE_TYPE_ICONS[step.node_type] || Circle;
  const nodeTypeLabel = NODE_TYPE_LABELS[step.node_type] || step.node_type;
  const content = step.content || {};
  const metadata = step.metadata || {};
  
  // Get the primary content field based on node type
  const primaryContent = content.message || content.question_text || content.prompt_text || content.title || "";
  
  return (
    <ScrollArea className="flex-1">
      <div className="p-6 space-y-6" data-testid="step-review-card">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-10 h-10 rounded-lg flex items-center justify-center",
              "bg-purple-500/20 text-purple-400"
            )}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-slate-100">
                Step {stepNumber}: {metadata.label || nodeTypeLabel}
              </h3>
              <p className="text-sm text-slate-400">
                {nodeTypeLabel} • {step.id}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {isEdited && (
              <Badge variant="outline" className="border-blue-500/30 text-blue-400">
                <Edit3 className="h-3 w-3 mr-1" />
                Edited
              </Badge>
            )}
            {isApproved && (
              <Badge className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                <Check className="h-3 w-3 mr-1" />
                Approved
              </Badge>
            )}
          </div>
        </div>
        
        <Separator className="bg-slate-700/50" />
        
        {/* Content Fields */}
        <div className="space-y-6">
          {/* Node Label */}
          <EditableField
            label="Label"
            value={metadata.label}
            field="metadata.label"
            nodeId={step.id}
            onSave={onEdit}
            isSaving={isSaving}
          />
          
          {/* Primary Content */}
          <EditableField
            label="Content"
            value={primaryContent}
            field="content.message"
            nodeId={step.id}
            onSave={onEdit}
            isSaving={isSaving}
            multiline
          />
          
          {/* Response Options (for question nodes) */}
          {step.node_type === "question" && content.responses && (
            <ResponseOptions
              responses={content.responses}
              nodeId={step.id}
              onSave={onEdit}
              isSaving={isSaving}
            />
          )}
          
          {/* Input Type (for input nodes) */}
          {step.node_type === "input" && content.input_type && (
            <div className="space-y-2">
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                Input Type
              </label>
              <Badge variant="outline" className="border-emerald-500/30 text-emerald-400">
                {content.input_type}
              </Badge>
            </div>
          )}
          
          {/* Consent-specific */}
          {step.node_type === "consent" && (
            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm text-amber-300">
                <Shield className="h-4 w-4 inline mr-2" />
                Consent text must be entered manually before publishing
              </p>
            </div>
          )}
          
          {/* Booking-specific */}
          {step.node_type === "booking" && (
            <div className="p-4 rounded-lg bg-pink-500/10 border border-pink-500/20">
              <p className="text-sm text-pink-300">
                <Calendar className="h-4 w-4 inline mr-2" />
                Connect your calendar in Funnel Settings
              </p>
            </div>
          )}
          
          {/* Routing Preview */}
          <RoutingPreview routing={routing} reviewOrder={reviewOrder} />
        </div>
      </div>
    </ScrollArea>
  );
}
